import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { Observable, Subject, concat, of, debounceTime, distinctUntilChanged, switchMap, tap, catchError, filter, take } from 'rxjs';

/**
 * Interface para itens do select
 */
export interface SelectItem {
  id?: string | number;
  name?: string;
  label?: string;
  centralCostNumber?: string;
  [key: string]: unknown;
}

export interface DynamicSelectConfig {
  class?: string;
  key: string;
  label: string;
  serviceName?: string;
  apiMethod?: string;
  bindLabel?: string;
  bindValue?: string;
  placeholder?: string;
  typeahead?: boolean;
  items?: Observable<SelectItem[]> | SelectItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  service?: any;
  labelColor?: string;
  required?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
  multiple?: boolean;
  hidden?: boolean;
}

export interface SelectValue {
  key: string;
  value: string | number | Date | SelectItem | (string | number | Date | SelectItem)[] | null;
}

@Component({
  selector: 'app-dy-select',
  templateUrl: './dynamic-select.component.html',
  styleUrls: ['./dynamic-select.component.scss']
})
export class DynamicSelectComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectConfig: DynamicSelectConfig[] = [];
  @Input() initialValues: { [key: string]: string | number | Date | SelectItem | (string | number)[] | null } = {};
  @Input() defaultLabelColor = 'var(--default-label-color, #495057)';
  @Input() readonly = false;
  @Input() isDefaultConfiguration = false;
  @Output() selectionChange = new EventEmitter<SelectValue[]>();

  selectedValues: { [key: string]: string | number | Date | SelectItem | (string | number)[] | null } = {};
  selectData: { [key: string]: Observable<SelectItem[]> } = {};
  selectLoading: { [key: string]: boolean } = {};
  selectInput$: { [key: string]: Subject<string> } = {};

  constructor(private injector: Injector) { }

  ngOnInit(): void {
    this.initializeConfigs();
    this.setInitialValues();
  }

  ngOnDestroy(): void {
    Object.values(this.selectInput$).forEach(subject => {
      if (subject && typeof subject.complete === 'function') {
        subject.complete();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectConfig'] && !changes['selectConfig'].firstChange) {
      this.initializeConfigs();
      this.setInitialValues();
    }

    // Detecta mudanças no initialValues e reaplica os valores
    if (changes['initialValues'] && !changes['initialValues'].firstChange) {
      this.setInitialValues();
    }
  }

  /**
   * Re-inicializa as configurações quando há mudanças dinâmicas
   */
  refreshConfigs(): void {
    this.initializeConfigs();
  }

  /**
   * Força a atualização de um campo específico (especialmente para campos sem typeahead)
   */
  forceFieldUpdate(key: string): void {
    const config = this.selectConfig.find(c => c.key === key);
    if (config) {
      // Limpa os dados atuais
      this.selectData[key] = of([]);

      // Re-inicializa o campo
      if (config.items) {
        this.selectData[key] = Array.isArray(config.items) ? of(config.items) : config.items;
      } else if (config.service) {
        this.initializeSelectWithService(config);
      } else if (config.serviceName && config.apiMethod) {
        this.initializeSelectWithDynamicInjection(config);
      }
    }
  }

  /**
   * Atualiza apenas um campo específico sem perder valores selecionados
   */
  updateFieldOnly(key: string): void {
    const config = this.selectConfig.find(c => c.key === key);
    if (config) {
      // Salva o valor atual
      const currentValue = this.selectedValues[key];

      // Re-inicializa apenas este campo
      this.selectedValues[key] = null;

      if (config.items) {
        this.selectData[key] = Array.isArray(config.items) ? of(config.items) : config.items;
      } else if (config.service) {
        this.initializeSelectWithService(config);
      } else if (config.serviceName && config.apiMethod) {
        this.initializeSelectWithDynamicInjection(config);
      }

      // Restaura o valor após um pequeno delay
      if (currentValue !== null) {
        setTimeout(() => {
          this.selectedValues[key] = currentValue;
        }, 100);
      }
    }
  }

  private initializeConfigs(): void {
    this.selectConfig.forEach(config => {
      this.selectedValues[config.key] = null;

      if (config.items) {
        // Usar dados estáticos ou observables fornecidos
        this.selectData[config.key] = Array.isArray(config.items) ? of(config.items) : config.items;
      } else if (config.service) {
        // Usar service diretamente injetado
        this.initializeSelectWithService(config);
      } else if (config.serviceName && config.apiMethod) {
        // Usar injeção dinâmica de service
        this.initializeSelectWithDynamicInjection(config);
      }
    });
  }

  private initializeSelectWithService(config: DynamicSelectConfig): void {
    this.selectLoading[config.key] = false;
    this.selectInput$[config.key] = new Subject<string>();

    try {
      const service = config.service;

      if (!service || !config.apiMethod) {
        console.warn(`Service ou apiMethod não fornecido para ${config.key}`);
        this.selectData[config.key] = of([]);
        return;
      }

      const apiMethod = config.apiMethod;

      if (config.typeahead) {
        // Para selects com typeahead (busca com digitação)
        this.selectData[config.key] = concat(
          of([]),
          this.selectInput$[config.key].pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.selectLoading[config.key] = true),
            switchMap((term: string) => {
              const method = service[apiMethod];
              if (typeof method === 'function') {
                const params = this.buildMethodParams(term, config.params);
                console.log(`🔧 Typeahead ${config.key} - chamando ${apiMethod} com parâmetros:`, params);
                return (method.call(service, ...params) as Observable<SelectItem[]>).pipe(
                  catchError(err => {
                    console.error(`Erro ao buscar dados para ${config.key}:`, err);
                    return of([]);
                  })
                );
              }
              return of([]);
            }),
            tap(() => this.selectLoading[config.key] = false)
          )
        );
      } else {
        // Para selects simples (carrega todos os dados de uma vez)
        const method = service[apiMethod];
        if (typeof method === 'function') {
          const params = this.buildMethodParams('', config.params);
          console.log(`🔧 Chamando ${apiMethod} com parâmetros:`, params);
          this.selectData[config.key] = (method.call(service, ...params) as Observable<SelectItem[]>).pipe(
            catchError(err => {
              console.error(`Erro ao carregar dados para ${config.key}:`, err);
              return of([]);
            })
          );
        } else {
          console.warn(`Método ${apiMethod} não encontrado no service para ${config.key}`);
          this.selectData[config.key] = of([]);
        }
      }
    } catch (error) {
      console.error(`Erro ao inicializar service para ${config.key}:`, error);
      this.selectData[config.key] = of([]);
    }
  }

  private initializeSelectWithDynamicInjection(config: DynamicSelectConfig): void {
    this.selectLoading[config.key] = false;
    this.selectInput$[config.key] = new Subject<string>();

    if (!config.serviceName || !config.apiMethod) {
      console.warn(`serviceName ou apiMethod não fornecido para ${config.key}`);
      this.selectData[config.key] = of([]);
      return;
    }

    try {
      // Nota: A injeção dinâmica por string não é type-safe
      // Recomenda-se usar a propriedade 'service' ao invés de 'serviceName'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const service: any = this.injector.get(config.serviceName as any);

      if (!service) {
        console.warn(`Service ${config.serviceName} não encontrado para ${config.key}`);
        this.selectData[config.key] = of([]);
        return;
      }

      const apiMethod = config.apiMethod;

      if (config.typeahead) {
        // Para selects com typeahead (busca com digitação)
        this.selectData[config.key] = concat(
          of([]),
          this.selectInput$[config.key].pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.selectLoading[config.key] = true),
            switchMap((term: string) => {
              const method = service[apiMethod];
              if (typeof method === 'function') {
                const params = this.buildMethodParams(term, config.params);
                console.log(`🔧 Typeahead ${config.key} - chamando ${apiMethod} com parâmetros:`, params);
                return (method.call(service, ...params) as Observable<SelectItem[]>).pipe(
                  catchError(err => {
                    console.error(`Erro ao buscar dados para ${config.key}:`, err);
                    return of([]);
                  })
                );
              }
              return of([]);
            }),
            tap(() => this.selectLoading[config.key] = false)
          )
        );
      } else {
        // Para selects simples (carrega todos os dados de uma vez)
        const method = service[apiMethod];
        if (typeof method === 'function') {
          const params = this.buildMethodParams('', config.params);
          this.selectData[config.key] = (method.call(service, ...params) as Observable<SelectItem[]>).pipe(
            catchError(err => {
              console.error(`Erro ao carregar dados para ${config.key}:`, err);
              return of([]);
            })
          );
        } else {
          console.warn(`Método ${apiMethod} não encontrado no service ${config.serviceName}`);
          this.selectData[config.key] = of([]);
        }
      }
    } catch (error) {
      console.error(`Erro ao injetar service ${config.serviceName} para ${config.key}:`, error);
      this.selectData[config.key] = of([]);
    }
  }

  private setInitialValues(): void {
    if (this.initialValues) {
      Object.keys(this.initialValues).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(this.selectedValues, key)) {
          const value = this.initialValues[key];
          const config = this.selectConfig.find(c => c.key === key);

          // Para campos com typeahead que têm valores iniciais, carrega os dados primeiro
          if (config && config.typeahead && value !== null && value !== undefined) {
            // Dispara uma busca vazia para carregar os dados iniciais
            this.loadInitialData(key);
          }

          this.selectedValues[key] = value;

          // Para campos cujos dados são carregados assincronamente (como role),
          // aguarda os dados serem carregados antes de reaplicar o valor
          if (value !== null && value !== undefined && this.selectData[key]) {
            // Aguarda até que os dados sejam carregados (array com itens)
            this.selectData[key].pipe(
              filter((items) => items && items.length > 0), // Aguarda dados reais (não array vazio)
              take(1) // Pega apenas a primeira emissão com dados
            ).subscribe(() => {
              // Dados carregados, força o ng-select a reavaliar
              setTimeout(() => {
                // Força uma nova atribuição para o ng-select detectar a mudança
                const currentValue = this.selectedValues[key];
                this.selectedValues[key] = null;
                setTimeout(() => {
                  this.selectedValues[key] = currentValue;
                }, 10);
              }, 50);
            });
          }
        }
      });
    }
  }

  onValueChange(key: string, value: string | number | Date | SelectItem | (string | number)[] | null): void {
    this.selectedValues[key] = value;
    this.emitSelectionChange();
  }

  onSelectInputChange(key: string, term: string): void {
    if (this.selectInput$[key]) {
      // Garante que apenas strings válidas sejam passadas como termo de busca
      // Evita que valores numéricos (como branchId) sejam confundidos com termos de busca
      const validTerm = typeof term === 'string' ? term : '';
      this.selectInput$[key].next(validTerm);
    }
  }

  onFieldFocus(): void {
    // Campo recebeu foco
  }

  onFieldBlur(key: string): void {
    // Quando o campo perde o foco, garante que uma busca vazia seja feita
    // para manter os dados carregados sem filtro de texto
    if (this.selectInput$[key]) {
      this.selectInput$[key].next('');
    }
  }

  private emitSelectionChange(): void {
    const selectedArray: SelectValue[] = Object.keys(this.selectedValues)
      .map(key => ({
        key,
        value: this.selectedValues[key]
      }))
      .filter(item => item.value !== null && item.value !== undefined && item.value !== '');

    this.selectionChange.emit(selectedArray);
  }

  /**
   * Limpa todos os valores selecionados
   */
  clearSelections(): void {
    Object.keys(this.selectedValues).forEach(key => {
      this.selectedValues[key] = null;
    });
    this.emitSelectionChange();
  }

  /**
   * Obtém o valor selecionado de um campo específico
   */
  getSelectedValue(key: string): string | number | Date | SelectItem | (string | number)[] | null {
    return this.selectedValues[key];
  }

  /**
   * Define o valor de um campo específico programaticamente
   */
  setValue(key: string, value: string | number | Date | SelectItem | (string | number)[] | null): void {
    if (Object.prototype.hasOwnProperty.call(this.selectedValues, key)) {
      this.selectedValues[key] = value;
      this.emitSelectionChange();
    }
  }

  /**
   * Força o carregamento inicial de dados para um campo com typeahead
   * Útil quando há valores iniciais que precisam ser mapeados para objetos
   */
  loadInitialData(key: string): void {
    if (this.selectInput$[key]) {
      // Dispara uma busca vazia para carregar os dados iniciais
      this.selectInput$[key].next('');
    }
  }

  /**
   * Obtém o valor de exibição de um item do select
   */
  getDisplayValue(item: SelectItem, config: DynamicSelectConfig): string {
    if (!item) return '';


    if (config.key === 'role') {
      if (item.id && item.name && item.area) {
        return `${item.id} - ${item.name} - ${item.area}`;
      }
    }

    if (config.bindLabel) {
      return String(item[config.bindLabel] || '');
    }


    // Caso especial para itens com nome e número de centro de custo
    if (item.name && item.centralCostNumber) {
      return `${item.name} - ${item.centralCostNumber}`;
    }

    return item.name || item.label || item.toString();
  }

  /**
   * Obtém os itens do select (dados)
   */
  getSelectItems(config: DynamicSelectConfig): Observable<SelectItem[]> {
    if (config.items) {
      return Array.isArray(config.items) ? of(config.items) : config.items;
    }

    if (this.selectData[config.key]) {
      return this.selectData[config.key];
    }

    return of([]);
  }

  /**
   * Constrói parâmetros para chamadas de método
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildMethodParams(term: string, params?: any): any[] {
    if (!params) {
      return term ? [term] : [];
    }

    // Para typeahead, primeiro parâmetro é o termo, depois os params
    if (term) {
      const paramValues = Object.keys(params)
        .map(key => params[key])
        .filter(value => value !== undefined);
      return [term, ...paramValues];
    }

    // Para chamadas simples, passa termo vazio e depois os params
    // IMPORTANTE: Para getAllAreas, sempre passa nameTerm primeiro, depois branchIds
    // Filtra valores undefined mas mantém a ordem
    const paramValues = Object.keys(params)
      .map(key => params[key])
      .filter(value => value !== undefined);
    return ['', ...paramValues];
  }
}